/**
 * <variant-picker> (snippets/variant-picker.liquid) resolves the selected
 * variant from a JSON script tag of product.variants (rendered once by
 * sections/main-product.liquid) and, on change:
 * - updates the price block (local data swap, no network round trip)
 * - updates the Add to cart button's hidden variant id, disabled state and
 *   label (see snippets/buy-buttons.liquid's data-buy-buttons-* hooks)
 * - shows/hides an "unavailable combination" note
 * - scrolls the gallery to the variant's featured media, if any
 * - pushes ?variant=<id> via history.replaceState
 * - dims (but keeps clickable) option values that don't resolve to an
 *   available variant given the rest of the current selection
 * - dispatches `variant:changed` on document with {detail: {variant}}
 *
 * The pill radios have no `name="id"` (a radio's value is one option's
 * text, not a resolved variant id) and buy-buttons.liquid's hidden input
 * ships without a `name` at all - see that snippet's comment. The very
 * first thing this element does once it's actually running is give that
 * hidden input `name="id"`, making it the live submitted field for as
 * long as JS keeps it in sync. Without JS, that `connectedCallback` never
 * runs, the input stays name-less (never submitted), and the real
 * fallback is the `<noscript>` `<select name="id" form="...">` rendered
 * by snippets/variant-picker.liquid.
 */
if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.scope = this.closest('[data-section-id]') || this;
      const jsonScript = this.scope.querySelector('[data-variant-json]');
      this.variants = jsonScript ? JSON.parse(jsonScript.textContent) : [];
      this.saleBadgeTemplate = jsonScript ? jsonScript.dataset.saleBadgeTemplate : '';
      this.unitPriceLabel = jsonScript ? jsonScript.dataset.unitPriceLabel : '';
      this.fieldsets = Array.from(this.querySelectorAll('[data-option-position]'));

      const idInput = this.scope.querySelector('[data-buy-buttons-variant-id]');
      if (idInput && idInput.name !== 'id') {
        idInput.name = 'id';
        /* Shopify's Buy it now may already have read this form and found no
           `id` (it then stays disabled). It re-reads only on a `value`
           attribute change, so make one. */
        idInput.setAttribute('value', idInput.value);
      }

      this.onChange = this.onChange.bind(this);
      this.addEventListener('change', this.onChange);

      this.refreshAvailability();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }

    onChange(event) {
      if (!event.target.matches('[data-option-value]')) return;
      const fieldset = event.target.closest('[data-option-position]');
      if (fieldset) {
        const valueLabel = fieldset.querySelector('[data-selected-value]');
        if (valueLabel) valueLabel.textContent = event.target.value;
      }
      this.snapToExisting(fieldset);
      this.refreshAvailability();
      this.applySelection();
    }

    // Choosing a value the current combination doesn't come in (Trial Pack
    // while QTY 20 is selected) moves the other options to the nearest
    // variant that has it - in stock first, then the one sharing the most
    // of the previous choices - instead of stranding the shopper on a
    // combination the product does not make.
    snapToExisting(changedFieldset) {
      const options = this.getSelectedOptions();
      if (this.findVariant(options)) return;
      const position = changedFieldset ? Number(changedFieldset.dataset.optionPosition) - 1 : -1;
      const candidates = this.variants.filter((variant) => position < 0 || variant.options[position] === options[position]);
      if (!candidates.length) return;
      const shared = (variant) => variant.options.reduce((n, value, index) => n + (value === options[index] ? 1 : 0), 0);
      candidates.sort((a, b) => (Number(b.available) - Number(a.available)) || (shared(b) - shared(a)));
      const target = candidates[0];
      this.fieldsets.forEach((fieldset, index) => {
        const radio = Array.from(fieldset.querySelectorAll('[data-option-value]')).find((r) => r.value === target.options[index]);
        if (radio && !radio.checked) {
          radio.checked = true;
          const valueLabel = fieldset.querySelector('[data-selected-value]');
          if (valueLabel) valueLabel.textContent = radio.value;
        }
      });
    }

    getSelectedOptions() {
      return this.fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector('[data-option-value]:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return this.variants.find((variant) =>
        variant.options.every((value, index) => value === options[index])
      );
    }

    applySelection() {
      const options = this.getSelectedOptions();
      const variant = this.findVariant(options);

      this.updatePrice(variant);
      this.updateBuyButton(variant);
      this.updateQuantityRule(variant);
      this.updateGallery(variant);
      this.updateUrl(variant);

      document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant: variant || null } }));
    }

    // #ProductPrice-* holds TWO siblings when snippets/price.liquid renders a
    // unit-priced variant: the `.price` div and a `<small class="unit-price">`
    // after it (see that snippet). This still rewrites the WHOLE container's
    // innerHTML (below), but `html` now includes both - the `.price` div AND,
    // when the new variant has one, the unit-price `<small>` - so the two
    // stay in sync with each other instead of a stale unit price surviving
    // (or a fresh one being wiped) on the next variant change.
    updatePrice(variant) {
      const priceEl = this.scope.querySelector('[id^="ProductPrice-"]');
      // A product before its drop shows "Price at drop" (snippets/price.liquid);
      // repainting here would reveal the price on the first swatch click.
      if (!priceEl || priceEl.querySelector('.price--tba')) return;
      if (!variant) return;

      const onSale = Boolean(variant.compare_at_price_amount) && variant.compare_at_price_amount > variant.price_amount;
      let html = '<div class="price' + (onSale ? ' price--on-sale' : '') + '">';
      html += '<span class="price__current">' + variant.price + '</span>';
      if (onSale) {
        const percent = Math.round(
          ((variant.compare_at_price_amount - variant.price_amount) / variant.compare_at_price_amount) * 100
        );
        const badgeText = (this.saleBadgeTemplate || '-__PERCENT__%').replace('__PERCENT__', String(percent));
        html += '<span class="price__compare"><s>' + variant.compare_at_price + '</s></span>';
        html += '<span class="badge badge--sale price__badge">' + badgeText + '</span>';
      }
      html += '</div>';

      // unit_price_html is pre-rendered server-side (money formatting, `| t`
      // pluralisation) for every variant - see sections/main-product.liquid's
      // variants_json - so this only ever reproduces markup Shopify itself
      // already formatted, never reimplements unit_price_with_measurement.
      if (variant.unit_price_html) {
        html += '<small class="unit-price text-small">';
        html += '<span class="visually-hidden">' + this.escapeHtml(this.unitPriceLabel) + '</span>';
        html += '<span class="unit-price__value">' + variant.unit_price_html + '</span>';
        html += '</small>';
      }

      priceEl.innerHTML = html;
    }

    // The one place this element inserts translated (merchant/shop-locale,
    // not visitor) text via innerHTML rather than a value Shopify itself
    // already produced as safe markup (unit_price_html, quantity_rule_note_html).
    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value || '';
      return div.innerHTML;
    }

    // Quantity rules are per-variant (snippets/buy-buttons.liquid only bakes
    // in the initially-selected one). Mirrors that snippet's own min/max/step
    // choice and re-renders the same "Minimum of N" / "Increments of N" note
    // it builds server-side, using quantity_rule_note_html - pre-rendered per
    // variant for the same reason unit_price_html is: no hand-rolled `| t`
    // pluralisation logic in JS.
    updateQuantityRule(variant) {
      const input = this.scope.querySelector('.buy-buttons__row [data-quantity-field]');
      const note = this.scope.querySelector('[data-buy-buttons-quantity-rule]');
      if (!variant) return;

      const min = variant.quantity_rule_min || 1;
      const max = variant.quantity_rule_max;
      const step = variant.quantity_rule_increment || 1;

      if (input) {
        input.min = String(min);
        // Correct the typed quantity only when the NEW variant's rule
        // actually makes it invalid (below min, above max, or off the
        // increment) - not unconditionally. A shopper who typed 4 and then
        // changed colour, on a product with no quantity rule at all, must
        // not be silently reset to 1: min is 1 either way, so there is
        // nothing to correct.
        const current = Number(input.value) || 0;
        let next = Math.max(min, current);
        if (max) next = Math.min(max, next);
        next = min + Math.ceil((next - min) / step) * step;
        if (next !== current) input.value = String(next);
        input.dataset.quantityRuleMin = String(min);
        if (max) {
          input.max = String(max);
        } else {
          input.removeAttribute('max');
        }
        if (step > 1) {
          input.step = String(step);
          input.dataset.quantityStep = String(step);
        } else {
          input.removeAttribute('step');
          delete input.dataset.quantityStep;
        }
      }

      if (note) {
        const hasNote = Boolean(variant.quantity_rule_note_html);
        note.innerHTML = hasNote ? variant.quantity_rule_note_html : '';
        note.hidden = !hasNote;
      }
    }

    updateBuyButton(variant) {
      const input = this.scope.querySelector('[data-buy-buttons-variant-id]');
      const submit = this.scope.querySelector('[data-buy-buttons-submit]');
      const label = this.scope.querySelector('[data-buy-buttons-label]');
      const availability = this.scope.querySelector('[data-availability-text]');

      if (input && variant) input.value = String(variant.id);

      const available = Boolean(variant && variant.available);
      if (submit) {
        submit.disabled = !available;
        submit.toggleAttribute('aria-disabled', !available);
      }
      if (label && submit) {
        if (available && submit.dataset.labelAddTemplate && variant) {
          label.textContent = submit.dataset.labelAddTemplate.replace('__PRICE__', variant.price);
        } else {
          label.textContent = available ? submit.dataset.labelAdd : submit.dataset.labelSoldOut;
        }
      }
      if (availability) {
        if (!variant) {
          availability.hidden = false;
          availability.textContent = availability.dataset.unavailableText || availability.textContent;
        } else if (!available) {
          availability.hidden = false;
        } else {
          availability.hidden = true;
        }
      }
    }

    updateGallery(variant) {
      if (!variant || !variant.featured_media_id) return;
      const gallery = this.scope.querySelector('media-gallery');
      if (gallery && typeof gallery.goToMedia === 'function') {
        gallery.goToMedia(String(variant.featured_media_id));
      }
    }

    updateUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    // Dims (but keeps clickable) any option value that has no available
    // variant once combined with the currently selected values of every
    // other option - sold out, or a combination the product does not make
    // at all (ClawGear x Waizowl: Trial Pack exists only as 80, so 20 / 40
    // / 160 must strike through when Trial Pack is chosen).
    refreshAvailability() {
      const currentOptions = this.getSelectedOptions();

      this.fieldsets.forEach((fieldset) => {
        const position = Number(fieldset.dataset.optionPosition) - 1;
        const radios = Array.from(fieldset.querySelectorAll('[data-option-value]'));

        radios.forEach((radio) => {
          const trialOptions = currentOptions.slice();
          trialOptions[position] = radio.value;
          // Every option must match, the tested one included. The old rule
          // skipped `position`, so "Trial Pack in 20" counted as the Trial
          // Pack in 80 and nothing was ever crossed out.
          const matches = this.variants.filter((variant) =>
            variant.options.every((value, index) => value === trialOptions[index])
          );
          const anyAvailable = matches.some((variant) => variant.available);
          const label = this.querySelector('label[for="' + radio.id + '"]');
          if (label) label.classList.toggle('is-unavailable', !anyAvailable);
        });
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
